import React, { useEffect, useState } from "react";
import { Plus, Tags, Trash2, Edit, MoreHorizontal, Power, Download, Upload, Search } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";

// R-015 — no `facility_id`: lookup categories have no facility column and neither
// FormRequest accepts one, so sending it was silently dropped.
const EMPTY_CAT = { name: "", code: "", scope: "global", description: "", status: "active" };
const EMPTY_VAL = { category_id: "", label: "", value: "", sort_order: 0, status: "active" };

export default function LookupsPage() {
  const [cats, setCats] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [values, setValues] = useState([]);
  const [catSearch, setCatSearch] = useState("");
  const [valSearch, setValSearch] = useState("");

  const [catSheet, setCatSheet] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState(EMPTY_CAT);
  const [catErrors, setCatErrors] = useState({});

  const [valSheet, setValSheet] = useState(false);
  const [editingVal, setEditingVal] = useState(null);
  const [valForm, setValForm] = useState(EMPTY_VAL);
  const [valErrors, setValErrors] = useState({});

  const [confirm, setConfirm] = useState({ open: false });

  async function loadCats() {
    try {
      const params = {};
      if (catSearch) params.q = catSearch;
      const res = await api.get("/lookups/categories", { params });
      // R-015 — Laravel envelope is `{ data: [...], meta: {...} }`, not `{ items }`.
      const rows = res.data.data ?? [];
      setCats(rows);
      if (!selectedCat && rows.length) setSelectedCat(rows[0]);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function loadValues() {
    if (!selectedCat) { setValues([]); return; }
    try {
      const params = { category_id: selectedCat.id };
      if (valSearch) params.q = valSearch;
      const res = await api.get("/lookups/values", { params });
      setValues(res.data.data ?? []);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { loadCats(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(loadCats, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [catSearch]);
  useEffect(() => { loadValues(); /* eslint-disable-next-line */ }, [selectedCat]);
  useEffect(() => { const t = setTimeout(loadValues, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [valSearch]);

  function openCreateCat() { setEditingCat(null); setCatForm(EMPTY_CAT); setCatErrors({}); setCatSheet(true); }
  function openEditCat(c) {
    setEditingCat(c);
    setCatForm({ name: c.name, code: c.code, scope: c.scope, description: c.description || "", status: c.status });
    setCatErrors({});
    setCatSheet(true);
  }
  async function saveCat() {
    const e = {};
    if (!catForm.name) e.name = "Required";
    if (!catForm.code) e.code = "Required";
    setCatErrors(e);
    if (Object.keys(e).length) return;
    try {
      if (editingCat) {
        /*
         * R-015 — `UpdateLookupCategoryRequest` accepts name/name_ar/scope/
         * description only. `code` is immutable and `status` is not editable
         * here: it moves via the activate/deactivate endpoints. Sending either
         * was silently dropped, so the status select in this sheet did nothing.
         */
        const p = { name: catForm.name, scope: catForm.scope, description: catForm.description };
        await api.put(`/lookups/categories/${editingCat.id}`, p);
        if (catForm.status !== editingCat.status) {
          await api.post(
            `/lookups/categories/${editingCat.id}/${catForm.status === "active" ? "activate" : "deactivate"}`
          );
        }
        toast.success("Category updated");
      } else {
        await api.post("/lookups/categories", catForm);
        toast.success("Category created");
      }
      setCatSheet(false);
      loadCats();
    } catch (err) { toast.error(formatApiError(err)); }
  }
  async function deleteCat(c) {
    try {
      await api.delete(`/lookups/categories/${c.id}`);
      toast.success("Category deleted");
      if (selectedCat?.id === c.id) setSelectedCat(null);
      loadCats();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function toggleCatStatus(c) {
    try {
      await api.post(`/lookups/categories/${c.id}/${c.status === "active" ? "deactivate" : "activate"}`);
      loadCats();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function openCreateVal() { setEditingVal(null); setValForm({ ...EMPTY_VAL, category_id: selectedCat?.id || "", sort_order: values.length }); setValErrors({}); setValSheet(true); }
  function openEditVal(v) {
    setEditingVal(v);
    setValForm({ category_id: v.category_id, label: v.label, value: v.value, sort_order: v.sort_order, status: v.status });
    setValErrors({});
    setValSheet(true);
  }
  async function saveVal() {
    const e = {};
    if (!valForm.label) e.label = "Required";
    if (!valForm.value) e.value = "Required";
    setValErrors(e);
    if (Object.keys(e).length) return;
    try {
      if (editingVal) {
        const p = { label: valForm.label, value: valForm.value, sort_order: Number(valForm.sort_order) || 0, status: valForm.status };
        await api.put(`/lookups/values/${editingVal.id}`, p);
        toast.success("Value updated");
      } else {
        await api.post("/lookups/values", { ...valForm, sort_order: Number(valForm.sort_order) || 0 });
        toast.success("Value created");
      }
      setValSheet(false);
      loadValues();
    } catch (err) { toast.error(formatApiError(err)); }
  }
  async function deleteVal(v) {
    try {
      await api.delete(`/lookups/values/${v.id}`);
      toast.success("Value deleted");
      loadValues();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function exportValuesCsv() {
    const rows = [["label", "value", "sort_order", "status"], ...values.map((v) => [v.label, v.value, v.sort_order, v.status])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedCat?.code || "values"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div data-testid="lookups-page">
      <PageHeader
        title="Lookups Management"
        subtitle="Reference data, picklists, and configurable enumerations across the platform."
        actions={
          <Button onClick={openCreateCat} data-testid="create-category-button" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
            <Plus className="w-4 h-4 mr-1.5" /> New Category
          </Button>
        }
      />

      <div className="grid grid-cols-12 gap-4">
        {/* Categories list */}
        <div className="col-span-12 lg:col-span-4 hah-card p-3 flex flex-col" style={{ maxHeight: 640 }}>
          <div className="flex items-center gap-2 px-2 py-2">
            <Search className="w-4 h-4 text-[#94A3B8]" />
            <input
              value={catSearch}
              onChange={(e) => setCatSearch(e.target.value)}
              placeholder="Search categories…"
              className="bg-transparent outline-none text-sm w-full"
              data-testid="lookup-cat-search"
            />
          </div>
          <div className="border-t border-[#E5E7EB] pt-2 overflow-y-auto space-y-1">
            {cats.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedCat(c)}
                data-testid={`lookup-cat-${c.code}`}
                className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-3 ${selectedCat?.id === c.id ? "bg-[#EAF8FA]" : "hover:bg-[#F5F7FA]"}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedCat?.id === c.id ? "bg-[#0B7E8C] text-white" : "bg-[#F5F7FA] text-[#0B7E8C]"}`}>
                  <Tags className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[#1F2937] text-sm truncate">{c.name}</div>
                  <div className="text-xs text-[#6B7280]">{c.code} · {c.scope}</div>
                </div>
                <StatusBadge status={c.status} />
              </button>
            ))}
          </div>
        </div>

        {/* Values */}
        <div className="col-span-12 lg:col-span-8 hah-card p-5">
          {selectedCat ? (
            <>
              <div className="flex items-start justify-between mb-4 gap-4">
                <div>
                  <div className="hah-section-title">Lookup values</div>
                  <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">{selectedCat.name}</h3>
                  <p className="text-sm text-[#6B7280] mt-1">{selectedCat.description || "No description"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => openEditCat(selectedCat)} className="border-[#E5E7EB]" data-testid="edit-category">
                    <Edit className="w-4 h-4 mr-1.5" /> Edit
                  </Button>
                  <Button variant="outline" onClick={() => toggleCatStatus(selectedCat)} className="border-[#E5E7EB]">
                    <Power className="w-4 h-4 mr-1.5" />
                    {selectedCat.status === "active" ? "Deactivate" : "Activate"}
                  </Button>
                  <Button variant="outline" onClick={exportValuesCsv} className="border-[#E5E7EB]" data-testid="export-values">
                    <Download className="w-4 h-4 mr-1.5" /> Export
                  </Button>
                  <Button onClick={openCreateVal} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="create-value-button">
                    <Plus className="w-4 h-4 mr-1.5" /> Value
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg px-3 py-1.5">
                <Search className="w-4 h-4 text-[#94A3B8]" />
                <input
                  value={valSearch}
                  onChange={(e) => setValSearch(e.target.value)}
                  placeholder="Search values…"
                  className="bg-transparent outline-none text-sm w-full"
                  data-testid="lookup-val-search"
                />
              </div>

              <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F7FA]">
                    <tr>
                      {["Label", "Value", "Sort", "Status", ""].map((h) => (
                        <th key={h} className="text-left text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] px-4 py-2.5">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {values.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-[#6B7280]">No values.</td></tr>
                    )}
                    {values.map((v) => (
                      <tr key={v.id} className="border-t border-[#E5E7EB] hover:bg-[#EAF8FA]/30">
                        <td className="px-4 py-2.5 font-medium text-[#1F2937]">{v.label}</td>
                        <td className="px-4 py-2.5 text-[#6B7280] font-mono text-xs">{v.value}</td>
                        <td className="px-4 py-2.5 text-[#6B7280]">{v.sort_order}</td>
                        <td className="px-4 py-2.5"><StatusBadge status={v.status} /></td>
                        <td className="px-4 py-2.5 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button data-testid={`val-actions-${v.id}`} className="w-8 h-8 rounded-md hover:bg-[#F5F7FA] flex items-center justify-center">
                                <MoreHorizontal className="w-4 h-4 text-[#6B7280]" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditVal(v)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                              <DropdownMenuItem className="text-[#EF4444]" onClick={() => setConfirm({ open: true, title: "Delete value", description: `Delete "${v.label}"?`, destructive: true, onConfirm: () => deleteVal(v) })}>
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex justify-end">
                <Button variant="outline" className="border-[#EF444433] text-[#EF4444] hover:bg-[#EF444412]"
                  onClick={() => setConfirm({ open: true, title: "Delete category", description: `Delete "${selectedCat.name}" and all its values?`, destructive: true, onConfirm: () => deleteCat(selectedCat) })}
                  data-testid="delete-category"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete category
                </Button>
              </div>
            </>
          ) : (
            <div className="text-sm text-[#6B7280] py-12 text-center">Select a category to manage its values.</div>
          )}
        </div>
      </div>

      {/* Category sheet */}
      <Sheet open={catSheet} onOpenChange={setCatSheet}>
        <SheetContent side="right" className="w-full sm:max-w-[520px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>{editingCat ? "Edit category" : "New category"}</SheetTitle>
            <SheetDescription>Configure lookup category scope and metadata.</SheetDescription>
          </SheetHeader>
          <div className="p-6">
            <FormSection title="General Information" testId="cat-form-general">
              <FormField label="Name" required error={catErrors.name}>
                <Input data-testid="cat-name" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} />
              </FormField>
              <FormField label="Code" required error={catErrors.code}>
                <Input data-testid="cat-code" disabled={!!editingCat} value={catForm.code} onChange={(e) => setCatForm({ ...catForm, code: e.target.value })} />
              </FormField>
              <FormField label="Scope">
                <Select value={catForm.scope} onValueChange={(v) => setCatForm({ ...catForm, scope: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global</SelectItem>
                    <SelectItem value="facility">Facility-specific</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Status">
                <Select value={catForm.status} onValueChange={(v) => setCatForm({ ...catForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Description" full>
                <Input value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} />
              </FormField>
            </FormSection>
          </div>
          <StickyActionBar>
            <Button variant="outline" onClick={() => setCatSheet(false)} className="border-[#E5E7EB]" data-testid="cat-form-cancel">Cancel</Button>
            <Button onClick={saveCat} data-testid="cat-form-save" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">{editingCat ? "Save changes" : "Create"}</Button>
          </StickyActionBar>
        </SheetContent>
      </Sheet>

      {/* Value sheet */}
      <Sheet open={valSheet} onOpenChange={setValSheet}>
        <SheetContent side="right" className="w-full sm:max-w-[500px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>{editingVal ? "Edit value" : "New value"}</SheetTitle>
            <SheetDescription>In category: {selectedCat?.name}</SheetDescription>
          </SheetHeader>
          <div className="p-6">
            <FormSection title="Value details" testId="val-form-general">
              <FormField label="Label" required error={valErrors.label}><Input data-testid="val-label" value={valForm.label} onChange={(e) => setValForm({ ...valForm, label: e.target.value })} /></FormField>
              <FormField label="Value" required error={valErrors.value}><Input data-testid="val-value" value={valForm.value} onChange={(e) => setValForm({ ...valForm, value: e.target.value })} /></FormField>
              <FormField label="Sort order"><Input type="number" value={valForm.sort_order} onChange={(e) => setValForm({ ...valForm, sort_order: e.target.value })} /></FormField>
              <FormField label="Status">
                <Select value={valForm.status} onValueChange={(v) => setValForm({ ...valForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </FormSection>
          </div>
          <StickyActionBar>
            <Button variant="outline" onClick={() => setValSheet(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={saveVal} data-testid="val-form-save" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">{editingVal ? "Save changes" : "Create"}</Button>
          </StickyActionBar>
        </SheetContent>
      </Sheet>

      <ConfirmDialog open={confirm.open} onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={confirm.title} description={confirm.description} destructive={confirm.destructive}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ open: false }); }} />
    </div>
  );
}
